import React, {
  FC,
  MouseEvent,
  RefObject,
  TouchEvent,
  memo,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import styled from '@emotion/styled';
import { useStorage } from '~Components/Storage';
import { useLogger, useSelector } from '~Hooks';
import { checkBrowserAllowClick } from '~utilities';
import { BACKSPACE, SPACE, rowsForCountry } from './layouts';

/** How long the fold-out takes - matches the modal card's own entrance. */
export const KEYBOARD_FOLD_MS = 260;

/** Hold backspace this long before it starts repeating, then one deletion per `REPEAT_EVERY_MS`. */
const REPEAT_AFTER_MS = 450;
const REPEAT_EVERY_MS = 60;

/**
 * Folds open from zero height. `grid-template-rows` is the one layout
 * property that can transition from nothing to "as tall as the content"
 * without measuring, so no JS runs during the fold.
 */
const Fold = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows ${KEYBOARD_FOLD_MS}ms cubic-bezier(0.22, 1, 0.36, 1);

  &.is-open {
    grid-template-rows: 1fr;
  }
`;

/** Clips the keys while folded; the keys slide down a touch as it opens. */
const Clip = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const Keys = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
  user-select: none;
  touch-action: manipulation;
  transform: translate3d(0, -12px, 0);
  opacity: 0;
  transition: transform ${KEYBOARD_FOLD_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${KEYBOARD_FOLD_MS}ms ease-out;

  .is-open & {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

/**
 * One key. Plain buttons with no handlers of their own - the keyboard
 * listens once on the container and finds the key through `data-key`, so
 * forty keys cost one listener and zero React state.
 */
const Key = styled.button`
  appearance: none;
  flex: 1 1 0;
  min-width: 0;
  max-width: 72px;
  height: 64px;
  padding: 0;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  font-family: var(--eposFontFamily);
  font-size: 26px;
  font-weight: 700;
  line-height: 64px;
  text-align: center;
  background: var(--colorBlueGray100);
  color: var(--colorBlueGray1200);
  transition: transform 0.1s ease-out, background-color 0.1s ease-out;

  &.is-touching {
    transform: scale(0.92);
    background: var(--eposBorderColor);
    filter: brightness(0.92);
  }

  &.is-wide {
    flex-basis: 112px;
    max-width: 112px;
  }

  &.is-space {
    flex-basis: 60%;
    max-width: 440px;
  }
`;

interface Props {
  /** The field the keys type into. Written straight to the DOM - the field must be uncontrolled. */
  inputRef: RefObject<HTMLInputElement>;
  /** Free text (a comment) gets a space bar and lower-case letters with a capital after a full stop. */
  freeText?: boolean;
  maxLength?: number;
  /** The root - add `is-open` to it to fold the keyboard out under the field. */
  foldRef: RefObject<HTMLDivElement>;
  /** After every key that changed the field - CodeInput moves its caret. Keep it stable. */
  onPress?: () => void;
  testId?: string;
}

/**
 * The EPOS on-screen keyboard: A–Z, 0–9, the company country's extra letters
 * (ÅÄÖ, ÆØ, Ñ - see layouts.ts, Sweden when unset), backspace, and a space
 * bar for free text. Replaces the native keyboard, so the field it drives is `readOnly`.
 * Keys go in at the field's caret (`selectionStart`), wherever it was put.
 * It never renders after mount: presses mutate the input's value directly
 * and the pressed look is a class toggled on the key element.
 *
 * The fold is driven from outside: add the `is-open` class to the root (via
 * `foldRef`) and it slides open under the field.
 */
const Keyboard: FC<Props> = ({
  inputRef,
  freeText = false,
  maxLength,
  testId,
  foldRef,
  onPress,
}) => {
  const logger = useLogger('epos-keyboard');
  const [storage] = useStorage();
  const allowBrowserClick = checkBrowserAllowClick(storage.allowBrowserClick);

  /** The company's country picks the extra letters. Null for companies created before the field existed. */
  const country = useSelector((state) => state.country);
  const rows = useMemo(() => {
    logger.info('Keyboard layout picked', { country: country ?? 'default' });
    return rowsForCountry(country);
  }, [country]);

  /** The key under the finger, if any. */
  const activeRef = useRef<HTMLButtonElement | null>(null);
  /** Hold-to-repeat timers for backspace. */
  const repeatTimeoutRef = useRef<number>(0);
  const repeatIntervalRef = useRef<number>(0);

  /**
   * Writes one key's effect into the field at its caret, like a real keyboard:
   * the caret is the input's own selection, so it moves with the text and
   * nothing renders. Respects `maxLength`.
   */
  const press = useCallback(
    (key: string) => {
      const field = inputRef.current;
      if (!field) return;
      const value = field.value;
      const start = field.selectionStart ?? value.length;
      const end = field.selectionEnd ?? start;
      const before = value.slice(0, start);
      const after = value.slice(end);

      /** Sets the text and puts the caret right after what was typed. */
      const write = (text: string, caret: number) => {
        field.value = text;
        field.setSelectionRange(caret, caret);
        onPress?.();
      };

      if (key === BACKSPACE) {
        if (start !== end) {
          write(before + after, start);
        } else if (start > 0) {
          write(before.slice(0, -1) + after, start - 1);
        }
        return;
      }
      if (maxLength !== undefined && before.length + after.length >= maxLength) return;

      if (key === SPACE) {
        /** No leading or doubled spaces in a comment. */
        if (before && !before.endsWith(' ') && !after.startsWith(' ')) {
          write(`${before} ${after}`, start + 1);
        }
        return;
      }

      /** Codes are uppercase; a comment reads like a sentence. */
      const sentenceStart = !before || /[.!?]\s$/.test(before);
      const char = freeText && !sentenceStart ? key.toLowerCase() : key;
      write(before + char + after, start + char.length);
    },
    [inputRef, freeText, maxLength, onPress],
  );

  const stopRepeat = useCallback(() => {
    window.clearTimeout(repeatTimeoutRef.current);
    window.clearInterval(repeatIntervalRef.current);
  }, []);

  /** Lets go of whatever key was pressed, firing it unless `cancel`. */
  const release = useCallback(
    (cancel: boolean) => {
      const active = activeRef.current;
      activeRef.current = null;
      stopRepeat();
      if (!active) return;
      active.classList.remove('is-touching');
      // A held backspace already did its work through the repeat timer.
      if (!cancel && !repeatIntervalRef.current) press(active.dataset.key ?? '');
      repeatIntervalRef.current = 0;
    },
    [press, stopRepeat],
  );

  const handleTouchStart = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      const target = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-key]');
      if (!target) return;
      // Keeps focus (and the caret) in the field instead of moving it to the key.
      event.preventDefault();
      event.stopPropagation();
      release(true);
      activeRef.current = target;
      target.classList.add('is-touching');

      if (target.dataset.key === BACKSPACE) {
        repeatTimeoutRef.current = window.setTimeout(() => {
          repeatIntervalRef.current = window.setInterval(() => press(BACKSPACE), REPEAT_EVERY_MS);
        }, REPEAT_AFTER_MS);
      }
    },
    [press, release],
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!activeRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      release(false);
    },
    [release],
  );

  const handleTouchCancel = useCallback(() => release(true), [release]);

  /** Mouse fallback for the browser, gated the same way `useButton` is. */
  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!allowBrowserClick) return;
      const target = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-key]');
      if (target) press(target.dataset.key ?? '');
    },
    [allowBrowserClick, press],
  );

  /** In the browser a mouse-down on a key would blur the field and hide its caret. */
  const handleMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  return (
    <Fold ref={foldRef} data-testid={testId}>
      <Clip>
        <Keys
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
          onMouseDown={handleMouseDown}
          onClick={handleClick}>
          {rows.map((row, index) => (
            <Row key={index}>
              {row.map((key) =>
                key === BACKSPACE ? (
                  <Key
                    key={key}
                    type="button"
                    data-key={key}
                    className="is-wide"
                    aria-label="backspace">
                    ⌫
                  </Key>
                ) : (
                  <Key key={key} type="button" data-key={key}>
                    {key}
                  </Key>
                ),
              )}
            </Row>
          ))}
          {freeText && (
            <Row>
              <Key type="button" data-key={SPACE} className="is-space" aria-label="space" />
            </Row>
          )}
        </Keys>
      </Clip>
    </Fold>
  );
};

export default memo(Keyboard);
